// Framework-agnostic WebSocket reconnect logic for the IRIS broker connection.
//
// This module is intentionally free of React and of the browser `WebSocket`
// type so it can be unit-tested with a mock socket and mock Keycloak refresh.
//
// Key behaviours (ticket C10):
//  - On every (re)connect attempt the CURRENT token is read via `getToken()`
//    (never a closure-captured value).
//  - If that token is expired / near-expiry the reconnector triggers a refresh
//    (`refreshToken()`) and uses the fresh token; it only gives up (surfacing a
//    disconnected state / re-login) when the refresh itself fails.
//  - Non-normal closes retry with capped exponential backoff + jitter instead
//    of a fixed interval, so an outage does not hammer the broker.
//  - A single close-handling path is used (no duplicated onclose logic).

// The subset of the WebSocket API this module relies on. `window.WebSocket`
// structurally satisfies this, so the provider can pass a real socket while
// tests pass a lightweight fake.
export interface WsLike {
  onopen: ((ev?: any) => void) | null
  onclose: ((ev: { code: number }) => void) | null
  onerror: ((ev?: any) => void) | null
  onmessage: ((ev: { data: any }) => void) | null
  close: (code?: number) => void
}

export const NORMAL_CLOSE = 1000

export interface BackoffOptions {
  baseMs: number
  factor: number
  maxMs: number
  // Additional random fraction of the (capped) delay added as jitter, in [0, jitterRatio).
  jitterRatio: number
}

export const DEFAULT_BACKOFF: BackoffOptions = {
  baseMs: 1000,
  factor: 2,
  maxMs: 30000,
  jitterRatio: 0.3,
}

// Pure, deterministic when `random` is fixed. `attempt` is 1-based
// (attempt 1 is the first retry after a failure).
export function computeBackoffDelay(
  attempt: number,
  opts: BackoffOptions = DEFAULT_BACKOFF,
  random: () => number = Math.random,
): number {
  const exp = opts.baseMs * Math.pow(opts.factor, Math.max(0, attempt - 1))
  const capped = Math.min(exp, opts.maxMs)
  const jitter = capped * opts.jitterRatio * random()
  return Math.round(capped + jitter)
}

export interface ReconnectorDeps {
  // Build the ws URL for a given token.
  buildUrl: (token: string) => string
  // Create (and return) a socket for the URL. The provider also stores the
  // socket reference here for message sending.
  createSocket: (url: string) => WsLike
  // Read the CURRENT token (e.g. a ref updated by Keycloak's onTokens).
  getToken: () => string
  // True when `token` is present and NOT expired/near-expiry.
  isTokenValid: (token: string) => boolean
  // Force a Keycloak refresh and resolve with the fresh token. Rejects when the
  // session is truly gone (refresh token expired) — that is the only give-up path.
  refreshToken: () => Promise<string>
  // Called with the new connected state on open/close/give-up.
  onConnectedChange: (connected: boolean) => void
  // Called with each parsed inbound message payload.
  onMessage: (payload: any) => void
  // Called when refresh fails and we can no longer recover (route to login).
  onGiveUp: () => void
  backoff?: BackoffOptions
  random?: () => number
  // Injectable timers (defaults to global) for deterministic tests.
  setTimeoutFn?: (cb: () => void, ms: number) => any
  clearTimeoutFn?: (handle: any) => void
  log?: (...args: any[]) => void
}

export interface Reconnector {
  // Begin connecting. Idempotent: a no-op while already running.
  start: () => void
  // Stop reconnecting and close the socket normally.
  stop: () => void
  // Current backoff attempt counter (exposed for tests/inspection).
  getAttempt: () => number
}

export function createWsReconnector(deps: ReconnectorDeps): Reconnector {
  const backoff = deps.backoff ?? DEFAULT_BACKOFF
  const random = deps.random ?? Math.random
  const setTimeoutFn = deps.setTimeoutFn ?? ((cb, ms) => setTimeout(cb, ms))
  const clearTimeoutFn = deps.clearTimeoutFn ?? ((h) => clearTimeout(h))
  const log = deps.log ?? (() => {})

  let running = false
  let attempt = 0
  let socket: WsLike | undefined
  let timer: any

  const clearTimer = () => {
    if (timer !== undefined) {
      clearTimeoutFn(timer)
      timer = undefined
    }
  }

  // Detach handlers so a socket we are discarding can never drive a reconnect.
  const detach = (s: WsLike | undefined) => {
    if (!s) return
    s.onopen = null
    s.onclose = null
    s.onerror = null
    s.onmessage = null
  }

  const scheduleReconnect = (delay: number) => {
    clearTimer()
    log(`WS reconnecting in ${delay} ms (attempt ${attempt})`)
    timer = setTimeoutFn(() => {
      timer = undefined
      void connect()
    }, delay)
  }

  const giveUp = () => {
    running = false
    clearTimer()
    deps.onConnectedChange(false)
    deps.onGiveUp()
  }

  // Single connect path used for the initial connect AND every reconnect.
  const connect = async () => {
    if (!running) return

    // Resolve the freshest valid token before opening the socket.
    let token = deps.getToken()
    if (!deps.isTokenValid(token)) {
      try {
        token = await deps.refreshToken()
      } catch (e) {
        log('WS token refresh failed; giving up', e)
        giveUp()
        return
      }
      // A stop() may have landed while we awaited the refresh.
      if (!running) return
      if (!token) {
        giveUp()
        return
      }
    }

    const s = deps.createSocket(deps.buildUrl(token))
    socket = s

    s.onopen = () => {
      log('WS OPENED')
      attempt = 0
      deps.onConnectedChange(true)
    }

    // The one and only close-handling path.
    s.onclose = (evt: { code: number }) => {
      log('WS CLOSED code =', evt.code)
      detach(s)
      if (socket === s) socket = undefined
      deps.onConnectedChange(false)

      if (!running) return

      if (evt.code === NORMAL_CLOSE) {
        // Deliberate close that wants an immediate reconnect.
        attempt = 0
        scheduleReconnect(0)
      } else {
        attempt += 1
        scheduleReconnect(computeBackoffDelay(attempt, backoff, random))
      }
    }

    s.onerror = (err?: any) => {
      log('WS ERROR', err)
    }

    s.onmessage = (evt: { data: any }) => {
      let payload
      try {
        payload = JSON.parse(evt.data)
      } catch {
        log('WS invalid JSON message', evt.data)
        return
      }
      deps.onMessage(payload)
    }
  }

  const start = () => {
    if (running) return
    running = true
    attempt = 0
    void connect()
  }

  const stop = () => {
    running = false
    clearTimer()
    const s = socket
    socket = undefined
    if (s) {
      detach(s)
      try {
        s.close(NORMAL_CLOSE)
      } catch {
        // ignore
      }
    }
    deps.onConnectedChange(false)
  }

  return { start, stop, getAttempt: () => attempt }
}
